import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Grid, Chip, Button, IconButton, Tooltip,
  TextField, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Slide
} from "@mui/material";
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  FileDownload as ExportIcon, Link as LinkIcon,
  Close as CloseIcon, InboxOutlined as EmptyIcon
} from "@mui/icons-material";
import { fetchData, postData, putData, deleteData } from "../../BaseUrl";
import DynamicTable from "../../DynamicComponent/DynamicTable";
import Toast from "../../DynamicComponent/Toast";
import ConfirmDialog from "../../DynamicComponent/ConfirmDialog";
import { exportToCSV } from "../../utils/tableUtils";

const GRADE_PILLS   = ["All", "MunMazhalai", "Mazhalai", "Nilai 1", "Nilai 2", "Nilai 3", "Nilai 4", "Nilai 5", "Nilai 6", "Nilai 7", "Nilai 8"];
const GRADE_OPTIONS = ["MunMazhalai", "Mazhalai", "Nilai 1", "Nilai 2", "Nilai 3", "Nilai 4", "Nilai 5", "Nilai 6", "Nilai 7", "Nilai 8"];
const SUBJECTS      = ["All Subjects", "Tamil Reading", "Tamil Writing", "Tamil Grammar", "Tamil Speaking", "Cultural Studies", "History"];
const TYPE_OPTIONS  = ["All", "Textbook", "Workbook"];

// Fallback mirrors StudentDashBoard/Textbooks/Textbooks.js data, extended with textbook/workbook link pairs
const FALLBACK_ROWS = [
  {
    id: "1", grade: "Nilai 1", subjectName: "Tamil",
    textbookTitle: "Tamil Mozhi Payirchi - Level 1",
    textbookLink: "https://drive.google.com/file/textbook-n1",
    workbookTitle: "Tamil Activity Book - Level 1",
    workbookLink: "https://drive.google.com/file/workbook-n1",
  },
  {
    id: "2", grade: "Nilai 2", subjectName: "Tamil",
    textbookTitle: "Tamil Mozhi Payirchi - Level 2",
    textbookLink: "https://drive.google.com/file/textbook-n2",
    workbookTitle: "Tamil Activity Book - Level 2",
    workbookLink: "https://drive.google.com/file/workbook-n2",
  },
  {
    id: "3", grade: "Nilai 3", subjectName: "Tamil",
    textbookTitle: "Tamil Ilakkanam - Level 3",
    textbookLink: "https://drive.google.com/file/textbook-n3",
    workbookTitle: "",
    workbookLink: "",
  },
  {
    id: "4", grade: "Nilai 4", subjectName: "Tamil",
    textbookTitle: "Tamil Padippu - Level 4",
    textbookLink: "https://drive.google.com/file/textbook-n4",
    workbookTitle: "Tamil Workbook - Level 4",
    workbookLink: "https://drive.google.com/file/workbook-n4",
  },
  {
    id: "5", grade: "Nilai 5", subjectName: "Tamil",
    textbookTitle: "Tamil Mozhi Payirchi - Level 5",
    textbookLink: "https://drive.google.com/file/textbook-n5",
    workbookTitle: "Tamil Activity Book - Level 5",
    workbookLink: "https://drive.google.com/file/workbook-n5",
  },
  {
    id: "6", grade: "Nilai 6", subjectName: "Tamil",
    textbookTitle: "Tamil Ilakkanam - Level 6",
    textbookLink: "https://drive.google.com/file/textbook-n6",
    workbookTitle: "",
    workbookLink: "",
  },
];

const EXPORT_COLUMNS = [
  { label: "Grade",           field: "grade" },
  { label: "Subject Name",    field: "subjectName" },
  { label: "Textbook Title",  field: "textbookTitle" },
  { label: "Textbook Link",   field: "textbookLink" },
  { label: "Workbook Title",  field: "workbookTitle" },
  { label: "Workbook Link",   field: "workbookLink" },
];

const TABLE_COLUMNS = [
  { label: "Grade",            field: "grade" },
  { label: "Subject Name",     field: "subjectName" },
  { label: "Textbook Title",   field: "textbookTitle" },
  { label: "Textbook Link",    field: "textbookLinkCell" },
  { label: "Workbook Title",   field: "workbookTitle" },
  { label: "Workbook Link",    field: "workbookLinkCell" },
  { label: "Actions",          field: "actions" },
];

const EMPTY_FORM = {
  grade: "", subjectName: "",
  textbookTitle: "", textbookLink: "",
  workbookTitle: "", workbookLink: "",
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const UrlChip = ({ url, label }) => {
  if (!url) return <Typography variant="caption" color="text.disabled">—</Typography>;
  return (
    <Chip
      icon={<LinkIcon sx={{ fontSize: 13 }} />}
      label={label || "Open"}
      size="small"
      clickable
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ backgroundColor: "#e3f2fd", color: "#1565c0", fontWeight: 500, maxWidth: 160 }}
    />
  );
};

const AdminTextbooks = () => {
  const [rawData, setRawData]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [filters, setFilters]         = useState({ grade: "All", subject: "All Subjects", type: "All" });
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [editTarget, setEditTarget]   = useState(null);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [formError, setFormError]     = useState("");
  const [saving, setSaving]           = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast]             = useState({ open: false, severity: "success", message: "" });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchData("admin/textbook/list");
      const records = result?.data?.records || result?.data || [];
      setRawData(Array.isArray(records) && records.length > 0 ? records : FALLBACK_ROWS);
    } catch {
      setRawData(FALLBACK_ROWS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openAdd = () => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditTarget(row);
    setForm({
      grade:         row.grade         || "",
      subjectName:   row.subjectName   || "",
      textbookTitle: row.textbookTitle || "",
      textbookLink:  row.textbookLink  || "",
      workbookTitle: row.workbookTitle || "",
      workbookLink:  row.workbookLink  || "",
    });
    setFormError("");
    setDialogOpen(true);
  };

  const handleFieldChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const validateForm = () => {
    if (!form.grade)       return "Grade is required.";
    if (!form.subjectName) return "Subject Name is required.";
    if (form.textbookTitle && !form.textbookLink) return "Textbook Link is required when a Textbook Title is provided.";
    if (form.textbookLink && !form.textbookTitle) return "Textbook Title is required when a Textbook Link is provided.";
    if (form.workbookTitle && !form.workbookLink) return "Workbook Link is required when a Workbook Title is provided.";
    if (form.workbookLink && !form.workbookTitle) return "Workbook Title is required when a Workbook Link is provided.";
    return "";
  };

  const handleSave = async () => {
    const err = validateForm();
    if (err) { setFormError(err); return; }
    setSaving(true);
    setFormError("");
    try {
      if (editTarget) {
        await putData(`admin/textbook/${editTarget.id}`, form);
        setToast({ open: true, severity: "success", message: "Textbook entry updated" });
      } else {
        await postData("admin/textbook", form);
        setToast({ open: true, severity: "success", message: "Textbook entry added" });
      }
      setDialogOpen(false);
      setEditTarget(null);
      setForm(EMPTY_FORM);
      loadData();
    } catch {
      setToast({ open: true, severity: "error", message: editTarget ? "Update failed" : "Add failed" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteData(`admin/textbook/${id}`);
      setToast({ open: true, severity: "success", message: "Textbook entry deleted" });
      setDeleteTarget(null);
      loadData();
    } catch {
      setToast({ open: true, severity: "error", message: "Delete failed" });
      setDeleteTarget(null);
    }
  };

  const handleExport = () => exportToCSV(EXPORT_COLUMNS, rawData, "textbooks.csv");

  // Client-side filtering
  const filteredData = rawData.filter((r) => {
    if (filters.grade !== "All" && r.grade !== filters.grade) return false;
    if (filters.subject !== "All Subjects" && r.subjectName !== filters.subject) return false;
    if (filters.type !== "All") {
      if (filters.type === "Textbook" && !r.textbookTitle) return false;
      if (filters.type === "Workbook" && !r.workbookTitle) return false;
    }
    return true;
  });

  const rows = filteredData.map((r) => ({
    ...r,
    textbookLinkCell: <UrlChip url={r.textbookLink} label="Textbook" />,
    workbookLinkCell: <UrlChip url={r.workbookLink} label="Workbook" />,
    actions: (
      <Box sx={{ display: "flex", gap: 0.5 }}>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => openEdit(r)}>
            <EditIcon fontSize="small" sx={{ color: "#2196f3" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={() => setDeleteTarget(r)}>
            <DeleteIcon fontSize="small" sx={{ color: "#f44336" }} />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  }));

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumb + Header */}
      <Typography variant="body2" color="text.secondary" mb={0.5}>Pages / Textbook & Workbook Management</Typography>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Textbook &amp; Workbook Management</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined" startIcon={<ExportIcon />} onClick={handleExport}
            sx={{ textTransform: "none" }}
          >
            Export CSV
          </Button>
          <Button
            variant="contained" startIcon={<AddIcon />} onClick={openAdd}
            sx={{ background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)", textTransform: "none" }}
          >
            Add Entry
          </Button>
        </Box>
      </Box>

      {/* Grade Chip Pills */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
        {GRADE_PILLS.map((g) => (
          <Chip
            key={g} label={g} clickable
            onClick={() => setFilters(p => ({ ...p, grade: g }))}
            sx={{
              backgroundColor: filters.grade === g ? "#244C77" : "#f5f5f5",
              color: filters.grade === g ? "#fff" : "text.primary",
              fontWeight: filters.grade === g ? 600 : 400,
              "&:hover": { backgroundColor: filters.grade === g ? "#244C77" : "#e0e0e0" },
            }}
          />
        ))}
      </Box>

      {/* Subject + Type Filters */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3, alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Subject</InputLabel>
          <Select value={filters.subject} label="Subject"
            onChange={(e) => setFilters(p => ({ ...p, subject: e.target.value }))}>
            {SUBJECTS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Type</InputLabel>
          <Select value={filters.type} label="Type"
            onChange={(e) => setFilters(p => ({ ...p, type: e.target.value }))}>
            {TYPE_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#244C77" }} />
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <EmptyIcon sx={{ fontSize: 64, color: "#ccc", mb: 2 }} />
          <Typography variant="body1" color="text.secondary">No textbook entries found.</Typography>
          <Typography variant="body2" color="text.secondary">Click "Add Entry" to create one.</Typography>
        </Box>
      ) : (
        <DynamicTable columns={TABLE_COLUMNS} rows={rows} />
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
        TransitionComponent={Transition}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" fontWeight="bold">
            {editTarget ? "Edit Textbook Entry" : "Add Textbook Entry"}
          </Typography>
          <IconButton onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Grade */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Grade (NILAI) *</InputLabel>
                <Select value={form.grade} label="Grade (NILAI) *" onChange={handleFieldChange("grade")}>
                  {GRADE_OPTIONS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* Subject Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Subject Name *" fullWidth size="small"
                value={form.subjectName} onChange={handleFieldChange("subjectName")}
              />
            </Grid>

            {/* Textbook Title */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Textbook Title" fullWidth size="small"
                placeholder="Optional"
                value={form.textbookTitle} onChange={handleFieldChange("textbookTitle")}
              />
            </Grid>

            {/* Textbook Link */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Textbook Link (URL)" fullWidth size="small"
                placeholder="https://..."
                value={form.textbookLink} onChange={handleFieldChange("textbookLink")}
              />
            </Grid>

            {/* Workbook Title */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Workbook Title" fullWidth size="small"
                placeholder="Optional"
                value={form.workbookTitle} onChange={handleFieldChange("workbookTitle")}
              />
            </Grid>

            {/* Workbook Link */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Workbook Link (URL)" fullWidth size="small"
                placeholder="https://..."
                value={form.workbookLink} onChange={handleFieldChange("workbookLink")}
              />
            </Grid>
          </Grid>

          {formError && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              {formError}
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setDialogOpen(false)} variant="outlined"
            sx={{ textTransform: "none", color: "#757575", borderColor: "#ccc" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave} variant="contained" disabled={saving}
            sx={{ textTransform: "none", background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)" }}
          >
            {saving ? "Saving..." : editTarget ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete Textbook Entry"
        message={`Are you sure you want to delete the entry for "${deleteTarget?.subjectName || ""}" (${deleteTarget?.grade || ""})?`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        severity="danger"
        onConfirm={() => handleDelete(deleteTarget?.id)}
        onCancel={() => setDeleteTarget(null)}
      />

      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />
    </Box>
  );
};

export default AdminTextbooks;
